import { IconNames } from "@blueprintjs/icons";
import { segmentTrackChangeChartMetric } from "in_queue/analytics";
import { PersistentSection } from "in_queue/components/common/PersistentSection";
import { TrackedProject } from "in_queue/types/projectType";
import { ScenarioMetricsMapping } from "in_queue/types/scenarioMetricsType";
import { ScenarioMetadata } from "in_queue/types/scenarioType";
import { useState } from "react";
import { ChartPlaceholder } from "./chartHelpers";
import { ChartMetric } from "./chartMetric";
import { ChartMetricSelect } from "./ChartMetricSelect";
import css from "./charts.module.scss";
import { DropoffChart } from "./DropoffChart";
import { SensitivityChart } from "./SensitivityChart";

const CHARTS_SECTION_TITLE = "Charts";
const CHARTS_OPEN_STORAGE_KEY = "iqProjectChartsOpen";
const CHARTS_SECTION_SUMMARY = "Scenario sensitivity and cost by dropoff";

interface ProjectChartsProps {
    /** Completed (RunSucceeded) scenarios for the current stage/tab. */
    scenarios: ScenarioMetadata[];
    /** All scenarios for this project — used to find the real base scenario. */
    allScenarios: ScenarioMetadata[];
    /** Current tracked project — used to compute per-scenario modified size. */
    project: TrackedProject;
    scenarioMetrics: ScenarioMetricsMapping | "loading";
}

/**
 * Orchestrator for the Project page charts. Shares the cost-basis toggle
 * between the Sensitivity and Dropoff charts (both axes use $/MW or
 * absolute $ in lockstep). Bails out when there aren't enough completed
 * scenarios to draw a useful comparison.
 */
export const ProjectCharts: React.FC<ProjectChartsProps> = ({
    scenarios,
    allScenarios,
    project,
    scenarioMetrics
}) => {
    const [metric, setMetric] = useState<ChartMetric>("COST_PER_MW");

    const chartsSection = (children: React.ReactNode) => (
        <PersistentSection
            title={CHARTS_SECTION_TITLE}
            summary={CHARTS_SECTION_SUMMARY}
            icon={IconNames.TIMELINE_BAR_CHART}
            storageKey={CHARTS_OPEN_STORAGE_KEY}
            toolbar={
                <ChartMetricSelect
                    value={metric}
                    onChange={(next) => {
                        segmentTrackChangeChartMetric({ metric: next });
                        setMetric(next);
                    }}
                />
            }
        >
            <div className={css.projectCharts}>{children}</div>
        </PersistentSection>
    );

    if (scenarioMetrics === "loading") {
        return chartsSection(
            <>
                <ChartPlaceholder title="Scenario sensitivity" />
                <ChartPlaceholder title="Cost by dropoff" />
            </>
        );
    }

    const completed = scenarios.filter(
        (s) => scenarioMetrics[s.scenarioId]?.sumAllocatedCost !== undefined
    );
    if (completed.length < 2) {
        return null;
    }

    return chartsSection(
        <>
            <SensitivityChart
                completed={completed}
                allScenarios={allScenarios}
                project={project}
                scenarioMetrics={scenarioMetrics}
                metric={metric}
            />
            <DropoffChart
                completed={completed}
                allScenarios={allScenarios}
                project={project}
                scenarioMetrics={scenarioMetrics}
                metric={metric}
            />
        </>
    );
};
